import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { AppShell } from "@/components/AppShell";
import { WorkerShell } from "@/components/worker/WorkerShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import SignDocument from "@/pages/SignDocument";
import Launcher from "@/pages/Launcher";
import Dashboard from "@/pages/Dashboard";
import StaffRecords from "@/pages/StaffRecords";
import RosterBoard from "@/pages/RosterBoard";
import Timesheets from "@/pages/Timesheets";
import Incidents from "@/pages/Incidents";
import IncidentDetail from "@/pages/IncidentDetail";
import IncidentNew from "@/pages/IncidentNew";
import JobBoard from "@/pages/JobBoard";
import Templates from "@/pages/Templates";
import Leave from "@/pages/Leave";
import Unavailability from "@/pages/Unavailability";
import Properties from "@/pages/Properties";
import Invoicing from "@/pages/Invoicing";
import Organisation from "@/pages/Organisation";
import DocumentLibrary from "@/pages/DocumentLibrary";
import ManagementTeam from "@/pages/ManagementTeam";
import DocumentView from "@/pages/DocumentView";
import DocumentApprovals from "@/pages/DocumentApprovals";
import PoliciesAI from "@/pages/PoliciesAI";
import NdisCommission from "@/pages/NdisCommission";
import PolicyCompliance from "@/pages/PolicyCompliance";
import RuleConfig from "@/pages/RuleConfig";
import AccountsSettings from "@/pages/AccountsSettings";
import PayrollSettings from "@/pages/PayrollSettings";
import Payroll from "@/pages/Payroll";
import DocumentCompliance from "@/pages/DocumentCompliance";
import Portal from "@/pages/Portal";
import Participants from "@/pages/Participants";
import StaffProfiles from "@/pages/StaffProfiles";
import DocumentReadReport from "@/pages/DocumentReadReport";
import Directory from "@/pages/Directory";
import Sms from "@/pages/Sms";
import BulkEmail from "@/pages/BulkEmail";
import NdisManagement from "@/pages/NdisManagement";
import IssueTracker from "@/pages/IssueTracker";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import DocumentBuilder from "@/pages/DocumentBuilder";
import AuditPrep from "@/pages/AuditPrep";
import MessagingSettings from "@/pages/MessagingSettings";
import HrDocumentTypes from "@/pages/HrDocumentTypes";
import HrNoteTypes from "@/pages/HrNoteTypes";
import OfficeNoteTypes from "@/pages/OfficeNoteTypes";
import ServiceAgreementTemplates from "@/pages/ServiceAgreementTemplates";
import HolidaysAdmin from "@/pages/HolidaysAdmin";
import SmtpSettings from "@/pages/SmtpSettings";
import EmailQueue from "@/pages/EmailQueue";
import ReminderEmails from "@/pages/ReminderEmails";
import AuthSettings from "@/pages/AuthSettings";
import RolesSettings from "@/pages/RolesSettings";
import SupportAccessSettings from "@/pages/SupportAccessSettings";
import SupportSignIn from "@/pages/SupportSignIn";
import AiSettings from "@/pages/AiSettings";
import DocumentsAiSettings from "@/pages/DocumentsAiSettings";
import SectionAiSettingsPage from "@/pages/SectionAiSettingsPage";
import GeneralSettings from "@/pages/GeneralSettings";
import AuditLog from "@/pages/AuditLog";
import WorkerRoster from "@/pages/worker/WorkerRoster";
import WorkerShiftDetail from "@/pages/worker/WorkerShiftDetail";
import WorkerParticipants from "@/pages/worker/WorkerParticipants";
import WorkerJobBoard from "@/pages/worker/WorkerJobBoard";
import WorkerLeave from "@/pages/worker/WorkerLeave";
import Help, { PortalHelp } from "@/pages/Help";
import WorkerParticipantDetail from "@/pages/worker/WorkerParticipantDetail";
import WorkerParticipantDocuments from "@/pages/worker/WorkerParticipantDocuments";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Pages need a permission (backend/permissions.py); without it, back to the launcher.
function Perm({ k, children }) {
  const { can } = useAuth();
  if (!(can.sees || can.has)(...[].concat(k))) return <Navigate to="/" replace />;
  return children;
}

function DocsGate({ children }) {
  const { can } = useAuth();
  if (!can.docs) return <Navigate to="/" replace />;
  return children;
}

// Management access is fetched after sign-in (Management Team membership), so
// wait for it rather than bouncing a team member who reloads the page.
function ManagementGate({ children }) {
  const { can } = useAuth();
  if (can.managementPending) return null;
  if (!can.management) return <Navigate to="/" replace />;
  return children;
}

function ExecutiveGate({ children }) {
  const { can } = useAuth();
  if (can.managementPending) return null;
  if (!can.executive) return <Navigate to="/" replace />;
  return children;
}

function OfficeRoutes() {
  const { isWorker, isPortal } = useAuth();
  if (isPortal) return <Navigate to="/portal" replace />;
  if (isWorker) return <Navigate to="/worker" replace />;
  return (
    <AppShell />
  );
}

function WorkerRoutes() {
  const { isWorker } = useAuth();
  if (!isWorker) return <Navigate to="/" replace />;
  return <WorkerShell />;
}

function PortalRoutes() {
  const { isPortal } = useAuth();
  if (!isPortal) return <Navigate to="/" replace />;
  return <Portal />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/support-sign-in" element={<SupportSignIn />} />
          <Route path="/sign/:token" element={<SignDocument />} />

          {/* Support Worker mobile app */}
          <Route path="/worker" element={<Protected><WorkerRoutes /></Protected>}>
            <Route index element={<WorkerRoster />} />
            <Route path="jobs" element={<WorkerJobBoard />} />
            <Route path="leave" element={<WorkerLeave />} />
            <Route path="participants" element={<WorkerParticipants />} />
          </Route>
          <Route path="/worker/help" element={<Protected><WorkerRoutes /></Protected>}>
            <Route index element={<Help basePath="/worker/help" theme="dark" />} />
            <Route path="*" element={<Help basePath="/worker/help" theme="dark" />} />
          </Route>
          <Route path="/worker/shift/:name" element={<Protected><WorkerRoutes /></Protected>}>
            <Route index element={<WorkerShiftDetail />} />
          </Route>
          <Route path="/worker/participant/:name" element={<Protected><WorkerRoutes /></Protected>}>
            <Route index element={<WorkerParticipantDetail />} />
          </Route>
          <Route path="/worker/participant/:name/documents/:category" element={<Protected><WorkerRoutes /></Protected>}>
            <Route index element={<WorkerParticipantDocuments />} />
          </Route>

          {/* Participant / guardian portal */}
          <Route path="/portal" element={<Protected><PortalRoutes /></Protected>} />
          <Route path="/portal/help" element={<Protected><PortalHelp /></Protected>} />
          <Route path="/portal/help/*" element={<Protected><PortalHelp /></Protected>} />

          {/* Office desk */}
          <Route element={<Protected><OfficeRoutes /></Protected>}>
            <Route path="/" element={<Launcher />} />
            <Route path="/help" element={<Help basePath="/help" theme="light" />} />
            <Route path="/help/*" element={<Help basePath="/help" theme="light" />} />
            <Route path="/dashboard" element={<Perm k="roster.view"><Dashboard /></Perm>} />
            <Route path="/hr/staff" element={<Perm k="hr.view"><StaffRecords /></Perm>} />
            <Route path="/roster" element={<Perm k="roster.view"><RosterBoard /></Perm>} />
            <Route path="/participants" element={<Perm k="participants.view"><Participants /></Perm>} />
            <Route path="/staff-profiles" element={<Perm k="staff_profiles.view"><StaffProfiles /></Perm>} />
            <Route path="/document-compliance-report" element={<Perm k="participant_docs.view"><DocumentReadReport /></Perm>} />
            <Route path="/directory" element={<Perm k="participants.view"><Directory /></Perm>} />
            <Route path="/sms" element={<Perm k="sms.send"><Sms /></Perm>} />
            <Route path="/ndis-management" element={<Perm k="participants.view"><NdisManagement /></Perm>} />
            <Route path="/bulk-email" element={<Perm k="bulk_email.send"><BulkEmail /></Perm>} />
            <Route path="/timesheets" element={<Perm k="timesheets.view"><Timesheets /></Perm>} />
            <Route path="/incidents" element={<Perm k="incidents.view"><Incidents /></Perm>} />
            <Route path="/incidents/new" element={<Perm k="incidents.create"><IncidentNew /></Perm>} />
            <Route path="/incidents/:name" element={<Perm k="incidents.view"><IncidentDetail /></Perm>} />
            <Route path="/job-board" element={<Perm k="roster.view"><JobBoard /></Perm>} />
            <Route path="/templates" element={<Perm k="templates.view"><Templates /></Perm>} />
            <Route path="/leave" element={<Perm k="leave.view"><Leave /></Perm>} />
            <Route path="/unavailability" element={<Perm k="unavailability.view"><Unavailability /></Perm>} />
            <Route path="/properties" element={<Perm k="properties.view"><Properties /></Perm>} />
            <Route path="/invoicing" element={<Perm k="invoicing.view"><Invoicing /></Perm>} />
            <Route path="/payroll" element={<Perm k="payroll.view"><Payroll /></Perm>} />
            <Route path="/organisation" element={<Perm k="org.view"><Organisation /></Perm>} />
            <Route path="/organisation/:kind" element={<Perm k="org.view"><Organisation /></Perm>} />
            <Route path="/documents/policies" element={<DocsGate><DocumentLibrary library="policies" /></DocsGate>} />
            <Route path="/documents/company" element={<DocsGate><DocumentLibrary library="company" /></DocsGate>} />
            <Route path="/documents/policies/:docId" element={<DocsGate><DocumentView library="policies" /></DocsGate>} />
            <Route path="/documents/company/:docId" element={<DocsGate><DocumentView library="company" /></DocsGate>} />
            <Route path="/documents/management" element={<ManagementGate><ManagementTeam key="management" team="management" /></ManagementGate>} />
            <Route path="/documents/executive" element={<ExecutiveGate><ManagementTeam key="executive" team="executive" /></ExecutiveGate>} />
            <Route path="/documents/ndis-commission" element={<ManagementGate><NdisCommission /></ManagementGate>} />
            <Route path="/documents/compliance" element={<ManagementGate><PolicyCompliance /></ManagementGate>} />
            <Route path="/documents/approvals" element={<ManagementGate><DocumentApprovals /></ManagementGate>} />
            <Route path="/documents/ask" element={<DocsGate><PoliciesAI /></DocsGate>} />
            <Route path="/settings/accounts" element={<Perm k="settings.accounts"><AccountsSettings /></Perm>} />
            <Route path="/settings/payroll" element={<Perm k="settings.payroll"><PayrollSettings /></Perm>} />
            <Route path="/settings/rostering" element={<Perm k="settings.rostering"><RuleConfig /></Perm>} />
            <Route path="/settings/documents" element={<Perm k="settings.document_builder"><DocumentBuilder /></Perm>} />
            <Route path="/settings/document-compliance" element={<Perm k="settings.document_compliance"><DocumentCompliance /></Perm>} />
            <Route path="/settings/audit-prep" element={<Perm k="settings.audit_prep"><AuditPrep /></Perm>} />
            <Route path="/settings/documents-ai" element={<Perm k="settings.documents_ai"><DocumentsAiSettings /></Perm>} />
            <Route path="/settings/ai/:section" element={<Perm k="settings.ai"><SectionAiSettingsPage /></Perm>} />
            <Route path="/settings/messaging" element={<Perm k="settings.messaging"><MessagingSettings /></Perm>} />
            <Route path="/settings/hr-documents" element={<Perm k="settings.hr_document_types"><HrDocumentTypes /></Perm>} />
            <Route path="/settings/hr-note-types" element={<Perm k="settings.hr_note_types"><HrNoteTypes /></Perm>} />
            <Route path="/settings/office-note-types" element={<Perm k="settings.office_note_types"><OfficeNoteTypes /></Perm>} />
            <Route path="/settings/sa-templates" element={<Perm k="settings.sa_templates"><ServiceAgreementTemplates /></Perm>} />
            <Route path="/settings/holidays" element={<Perm k="settings.holidays"><HolidaysAdmin /></Perm>} />
            <Route path="/settings/email" element={<Perm k="settings.email"><SmtpSettings /></Perm>} />
            <Route path="/settings/email-queue" element={<Perm k="settings.email_queue"><EmailQueue /></Perm>} />
            <Route path="/settings/reminders" element={<Perm k="settings.reminders"><ReminderEmails /></Perm>} />
            <Route path="/settings/general" element={<Perm k="settings.general"><GeneralSettings /></Perm>} />
            <Route path="/settings/ai" element={<Perm k="settings.ai"><AiSettings /></Perm>} />
            <Route path="/settings/auth" element={<Perm k="settings.users"><AuthSettings /></Perm>} />
            <Route path="/settings/roles" element={<Perm k="settings.users"><RolesSettings /></Perm>} />
            <Route path="/settings/support-access" element={<Perm k="support.grant"><SupportAccessSettings /></Perm>} />
            <Route path="/rules" element={<Navigate to="/settings/rostering" replace />} />
            <Route path="/audit" element={<Perm k="system.audit_log"><AuditLog /></Perm>} />
            <Route path="/issues" element={<Perm k="system.issue_tracker"><IssueTracker /></Perm>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FeedbackWidget />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
